/**
 * V70 Tournament — 관리자 설정 페이지
 *
 * 사용자 지시 반영:
 *   - 공개 ON/OFF, 공개 대상 (전사/한국/대련/특정 인물) 설정
 *   - 특정 인물 → 직원 검색 modal + 다중 선택
 *   - 기본값 OFF
 *   - 최근 변경 이력 10건 노출
 *   - system_admin 전용 (BE 가 재검증, FE 는 사전 안내)
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { getFunctions } from '../../firebase/utils';
import { useAuthContext } from '../../shared/hooks/useAuthContext';
import { useEmployees } from '../../shared/hooks/useEmployees';
import { hasRole } from '../../utils/rbac';
import { acidRainApi } from '../tournament/acidRain/serverApi';
import type { LeaderboardMeta } from '../tournament/acidRain/serverApi';
import type { CoinPolicy, CoinPolicyChange, CoinPolicyMode } from '../tournament/acidRain/types';
import { DEFAULT_COIN_POLICY } from '../tournament/acidRain/types';
import { marbleApi } from '../tournament/marble/mock/serverApi';
import { OpenPeriodAdminSection } from '../tournament/shared/OpenPeriodAdminSection';

type AudienceType = 'all' | 'kr' | 'cn' | 'specific';

function describeCoinPolicy(p: CoinPolicy): string {
  return p.mode === 'unlimited' ? '무제한' : `제한(일일 ${p.dailyLimit}회)`;
}

interface Settings {
  enabled: boolean;
  audienceType: AudienceType;
  allowedUserIds: string[];
  updatedAt: string | null;
  updatedByName: string | null;
}

interface HistoryItem {
  id: string;
  previousSettings: Partial<Settings> | null;
  newSettings: Partial<Settings>;
  changeType: string;
  changedAt: string | null;
  changedByName: string | null;
}

export function TournamentAdminSettingsPage() {
  const authContext = useAuthContext();
  const isSystemAdmin = hasRole(authContext, 'system_admin');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const [enabled, setEnabled] = useState(false);
  const [audienceType, setAudienceType] = useState<AudienceType>('all');
  const [allowedUserIds, setAllowedUserIds] = useState<string[]>([]);
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [updatedByName, setUpdatedByName] = useState<string | null>(null);
  const [history, setHistory] = useState<HistoryItem[]>([]);

  const [showPicker, setShowPicker] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // ─── 코인 정책 (타자왕) ───
  const [coinPolicyLoaded, setCoinPolicyLoaded] = useState<CoinPolicy | null>(null);
  const [coinMode, setCoinMode] = useState<CoinPolicyMode>(DEFAULT_COIN_POLICY.mode);
  const [coinDailyLimit, setCoinDailyLimit] = useState<number>(DEFAULT_COIN_POLICY.dailyLimit);
  const [coinSaving, setCoinSaving] = useState(false);
  const [coinErr, setCoinErr] = useState<string | null>(null);
  const [coinMsg, setCoinMsg] = useState<string | null>(null);
  const [coinHistory, setCoinHistory] = useState<CoinPolicyChange[]>([]);

  const loadCoinPolicy = useCallback(async () => {
    try {
      const [p, h] = await Promise.all([
        acidRainApi.getCoinPolicy(),
        acidRainApi.getCoinPolicyHistory({ limit: 10 }),
      ]);
      setCoinPolicyLoaded(p);
      setCoinMode(p.mode);
      setCoinDailyLimit(p.dailyLimit);
      setCoinHistory(h);
    } catch {
      /* 조회 실패는 치명적이지 않음 */
    }
  }, []);

  const saveCoinPolicy = async () => {
    if (!isSystemAdmin) return;
    setCoinSaving(true);
    setCoinErr(null);
    setCoinMsg(null);
    try {
      if (coinMode === 'limited') {
        if (!Number.isInteger(coinDailyLimit)) throw new Error('일일 플레이 가능 횟수는 정수여야 합니다.');
        if (coinDailyLimit < 1 || coinDailyLimit > 100) throw new Error('일일 플레이 가능 횟수는 1~100 사이여야 합니다.');
      }
      const clientRequestId = `coinpol-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      const r = await acidRainApi.updateCoinPolicy({
        clientRequestId,
        policy: { mode: coinMode, dailyLimit: coinDailyLimit },
      });
      setCoinMsg(`코인 정책 저장 완료 — ${r.next.mode === 'unlimited' ? '무제한' : `일일 ${r.next.dailyLimit}회 제한`}${r.isMock ? ' (mock)' : ''}`);
      await loadCoinPolicy();
    } catch (e: any) {
      setCoinErr(e?.message || '저장 실패');
    } finally {
      setCoinSaving(false);
    }
  };

  // ─── V70 오픈 기간 (타자왕/마블) ───
  const [acidPeriod, setAcidPeriod] = useState<{ openStartAt: number | null; openEndAt: number | null }>({ openStartAt: null, openEndAt: null });
  const [acidEnabled, setAcidEnabled] = useState<boolean>(true);
  const [marblePeriod, setMarblePeriod] = useState<{ openStartAt: number | null; openEndAt: number | null }>({ openStartAt: null, openEndAt: null });
  const [marbleEnabled, setMarbleEnabled] = useState<boolean>(true);

  const loadPeriods = useCallback(async () => {
    try {
      const [acidS, marbleS] = await Promise.all([
        acidRainApi.getSettings(),
        marbleApi.getSettings(),
      ]);
      setAcidPeriod({ openStartAt: acidS.openStartAt ?? null, openEndAt: acidS.openEndAt ?? null });
      setAcidEnabled(acidS.enabled);
      setMarblePeriod({ openStartAt: marbleS.eventStartAt ?? null, openEndAt: marbleS.eventEndAt ?? null });
      setMarbleEnabled(marbleS.enabled);
    } catch { /* ignore */ }
  }, []);

  // ─── 리더보드 초기화 (타자왕) ───
  const [lbMeta, setLbMeta] = useState<LeaderboardMeta | null>(null);
  const [resetStep, setResetStep] = useState<0 | 1 | 2>(0); // 0 닫힘, 1 사유 입력, 2 최종 확인
  const [resetReason, setResetReason] = useState('');
  const [resetBusy, setResetBusy] = useState(false);
  const [resetErr, setResetErr] = useState<string | null>(null);
  const [resetMsg, setResetMsg] = useState<string | null>(null);

  const loadLbMeta = useCallback(async () => {
    try {
      const meta = await acidRainApi.getLeaderboardMeta();
      setLbMeta(meta);
    } catch {
      // 조회 실패는 치명적이지 않음
    }
  }, []);

  useEffect(() => {
    if (isSystemAdmin) {
      void loadLbMeta();
      void loadCoinPolicy();
      void loadPeriods();
    }
  }, [isSystemAdmin, loadLbMeta, loadCoinPolicy, loadPeriods]);

  const closeResetModal = () => {
    setResetStep(0);
    setResetReason('');
    setResetErr(null);
  };

  const executeReset = async () => {
    if (!isSystemAdmin) return;
    setResetBusy(true);
    setResetErr(null);
    setResetMsg(null);
    try {
      const clientRequestId = `lbreset-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      const r = await acidRainApi.resetLeaderboard({ clientRequestId, reason: resetReason.trim() });
      setResetMsg(`리더보드 초기화 완료 (epoch ${r.epoch}${r.isMock ? ', mock' : ''}).`);
      await loadLbMeta();
      closeResetModal();
    } catch (e: any) {
      setResetErr(e?.message || '초기화 실패');
    } finally {
      setResetBusy(false);
    }
  };

  const { employees: allEmployees } = useEmployees({ status: 'active_or_on_leave' });
  const employeeMap = useMemo(() => {
    const m = new Map<string, { nameKo: string; employeeNumber?: string; companyCode?: string }>();
    for (const e of allEmployees || []) {
      m.set(e.id, { nameKo: e.nameKo || '', employeeNumber: (e as any).employeeNumber, companyCode: (e as any).companyCode });
    }
    return m;
  }, [allEmployees]);

  const loadSettings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const functions = getFunctions();
      if (!functions) throw new Error('functions 미초기화');
      const getFn = httpsCallable<{}, { success: boolean; enabled: boolean; audienceType: AudienceType; allowedUserIds: string[]; updatedAt: string | null; updatedByName: string | null }>(functions, 'getTournamentSettings');
      const r = await getFn({});
      setEnabled(r.data.enabled);
      setAudienceType(r.data.audienceType);
      setAllowedUserIds(r.data.allowedUserIds || []);
      setUpdatedAt(r.data.updatedAt);
      setUpdatedByName(r.data.updatedByName);

      const histFn = httpsCallable<{ limit: number }, { success: boolean; items: HistoryItem[] }>(functions, 'getTournamentSettingsHistory');
      const h = await histFn({ limit: 10 });
      setHistory(h.data.items || []);
    } catch (e: any) {
      setError(e?.message || '설정 조회 실패');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isSystemAdmin) loadSettings();
    else setLoading(false);
  }, [isSystemAdmin, loadSettings]);

  const handleSave = async () => {
    if (!isSystemAdmin) return;
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      if (enabled && audienceType === 'specific' && allowedUserIds.length === 0) {
        throw new Error('특정 인물 선택 시 최소 1명 지정 필요합니다.');
      }
      const functions = getFunctions();
      if (!functions) throw new Error('functions 미초기화');
      const upd = httpsCallable<{ enabled: boolean; audienceType: AudienceType; allowedUserIds: string[] }, { success: boolean }>(functions, 'updateTournamentSettings');
      await upd({ enabled, audienceType, allowedUserIds: audienceType === 'specific' ? allowedUserIds : [] });
      setMessage('저장 완료');
      await loadSettings();
    } catch (e: any) {
      setError(e?.message || '저장 실패');
    } finally {
      setSaving(false);
    }
  };

  const toggleUser = (uid: string) => {
    setAllowedUserIds((prev) => prev.includes(uid) ? prev.filter((x) => x !== uid) : [...prev, uid]);
  };

  const filteredEmployees = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const list = allEmployees || [];
    if (!q) return list.slice(0, 50);
    return list.filter((e) =>
      (e.nameKo || '').toLowerCase().includes(q) ||
      ((e as any).employeeNumber || '').toLowerCase().includes(q)
    ).slice(0, 50);
  }, [allEmployees, searchQuery]);

  if (!isSystemAdmin) {
    return (
      <div style={{ padding: 24, textAlign: 'center', color: 'var(--color-red-600)' }}>
        <h2>접근 권한 없음</h2>
        <p>이 페이지는 system_admin 전용입니다.</p>
      </div>
    );
  }

  if (loading) return <div style={{ padding: 24 }}>로딩 중...</div>;

  return (
    <div style={{ padding: 24, maxWidth: 900 }}>
      <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 8 }}>게임대회 설정</h1>
      <p style={{ color: 'var(--color-gray-600)', marginBottom: 24, fontSize: 13 }}>
        공개 ON/OFF 및 대상자 지정. 기본값은 OFF 입니다. 관리자라도 OFF 상태에서는 사용자 메뉴에 접근할 수 없습니다 — 테스트는 ON + 특정 인물에서 본인 지정 후 가능합니다.
      </p>

      {error && <div style={{ background: 'var(--color-red-50)', color: 'var(--color-red-700)', padding: 12, borderRadius: 6, marginBottom: 16 }}>{error}</div>}
      {message && <div style={{ background: 'var(--color-green-50)', color: 'var(--color-green-700)', padding: 12, borderRadius: 6, marginBottom: 16 }}>{message}</div>}

      {/* ON/OFF */}
      <section style={{ padding: 16, border: '1px solid var(--color-gray-200)', borderRadius: 8, marginBottom: 16 }}>
        <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 12 }}>공개 상태</h2>
        <label style={{ display: 'flex', gap: 10, alignItems: 'center', cursor: 'pointer' }}>
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
          <span style={{ fontSize: 14, fontWeight: 500 }}>{enabled ? 'ON (사용자 메뉴 노출)' : 'OFF (비공개)'}</span>
        </label>
      </section>

      {/* 공개 대상 */}
      <section style={{ padding: 16, border: '1px solid var(--color-gray-200)', borderRadius: 8, marginBottom: 16, opacity: enabled ? 1 : 0.5 }}>
        <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 12 }}>공개 대상</h2>
        {[
          { v: 'all', label: '전사 — 모든 활성 직원' },
          { v: 'kr', label: '한국지사만 — companyCode=KR 활성 직원' },
          { v: 'cn', label: '대련지사만 — companyCode=CN 활성 직원' },
          { v: 'specific', label: '특정 인물만 — 지정 직원만' },
        ].map((opt) => (
          <label key={opt.v} style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '6px 0', cursor: enabled ? 'pointer' : 'not-allowed' }}>
            <input
              type="radio"
              name="audience"
              value={opt.v}
              disabled={!enabled}
              checked={audienceType === opt.v}
              onChange={() => setAudienceType(opt.v as AudienceType)}
            />
            <span style={{ fontSize: 13 }}>{opt.label}</span>
          </label>
        ))}

        {enabled && audienceType === 'specific' && (
          <div style={{ marginTop: 12, padding: 12, background: 'var(--color-gray-50)', borderRadius: 6 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <strong style={{ fontSize: 13 }}>지정된 참여자 ({allowedUserIds.length}명)</strong>
              <button onClick={() => setShowPicker(true)} style={{ padding: '6px 12px', fontSize: 12, background: 'var(--color-primary)', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer' }}>+ 추가/변경</button>
            </div>
            {allowedUserIds.length === 0 && <div style={{ fontSize: 12, color: 'var(--color-gray-500)' }}>최소 1명 지정 필요</div>}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {allowedUserIds.map((uid) => {
                const e = employeeMap.get(uid);
                return (
                  <span key={uid} style={{ padding: '4px 8px', background: 'white', border: '1px solid var(--color-gray-300)', borderRadius: 4, fontSize: 12 }}>
                    {e ? `${e.nameKo} (${e.employeeNumber || uid.slice(0, 8)}) · ${e.companyCode || '?'}` : uid}
                    <button onClick={() => toggleUser(uid)} style={{ marginLeft: 6, background: 'none', border: 'none', color: 'var(--color-red-500)', cursor: 'pointer' }}>×</button>
                  </span>
                );
              })}
            </div>
          </div>
        )}
      </section>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginBottom: 32 }}>
        <button onClick={handleSave} disabled={saving} style={{ padding: '10px 20px', fontSize: 14, fontWeight: 600, background: 'var(--color-primary)', color: 'white', border: 'none', borderRadius: 4, cursor: saving ? 'not-allowed' : 'pointer' }}>
          {saving ? '저장 중...' : '저장'}
        </button>
      </div>

      {updatedAt && (
        <div style={{ fontSize: 12, color: 'var(--color-gray-500)', marginBottom: 24 }}>
          최종 변경: {updatedAt} · {updatedByName || '-'}
        </div>
      )}

      {/* 게임 관리 — 타자왕 (Phase 2 Mock 인터페이스) */}
      <section style={{ padding: 16, border: '1px solid var(--color-gray-200)', borderRadius: 8, marginBottom: 24 }}>
        <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>게임 관리 — 엔글 타자왕</h2>
        <p style={{ color: 'var(--color-gray-600)', fontSize: 12, marginBottom: 16 }}>
          Phase 2 에서는 UI 와 API 인터페이스만 제공되며, 실제 Firestore 반영은 Phase 3 BE 에서 완성됩니다.
          <br />
          <span style={{ color: 'var(--color-gray-500)' }}>저장 범위 (mock): 설정과 당일 사용 이력은 브라우저 localStorage 에만 저장됩니다. 다른 사용자/다른 브라우저와 공유되지 않습니다.</span>
        </p>

        {/* ─── V70 오픈 기간 (타자왕) ─── */}
        <OpenPeriodAdminSection
          gameLabel="엔글 타자왕"
          enabled={acidEnabled}
          initialValue={acidPeriod}
          mockBadge="Phase 2 mock — localStorage"
          onSave={async (v) => {
            const r = await acidRainApi.updateOpenPeriod(v);
            setAcidPeriod({ openStartAt: r.openStartAt, openEndAt: r.openEndAt });
          }}
        />

        {/* ─── 코인 정책 ─── */}
        <div style={{ padding: 14, background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 6, marginBottom: 16 }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 8, color: '#0369a1' }}>플레이 코인 정책</h3>
          <div style={{ fontSize: 12, color: '#0c4a6e', marginBottom: 10, lineHeight: 1.7 }}>
            현재 적용 중: <strong>
              {coinPolicyLoaded == null
                ? '로딩 중…'
                : coinPolicyLoaded.mode === 'unlimited'
                ? '무제한 (코인 차감 없음)'
                : `제한 — 일일 ${coinPolicyLoaded.dailyLimit}회`}
            </strong>
          </div>
          <div style={{ marginBottom: 10 }}>
            {[
              { v: 'unlimited', label: '제한 없음 (무제한 플레이)', desc: '코인 차감 없이 플레이 가능. UI 에 "무제한" 표시.' },
              { v: 'limited', label: '제한 있음 (일일 플레이 가능 횟수 지정)', desc: '세션 시작 성공 시 1회 차감. 매일 00:00 KST 자동 갱신.' },
            ].map((opt) => (
              <label key={opt.v} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '6px 0', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="coinMode"
                  value={opt.v}
                  checked={coinMode === opt.v}
                  onChange={() => setCoinMode(opt.v as CoinPolicyMode)}
                  style={{ marginTop: 3 }}
                />
                <div style={{ fontSize: 13 }}>
                  <div style={{ fontWeight: 500 }}>{opt.label}</div>
                  <div style={{ color: 'var(--color-gray-600)', fontSize: 11 }}>{opt.desc}</div>
                </div>
              </label>
            ))}
          </div>

          <div style={{ padding: '8px 12px', background: 'white', border: '1px solid #e0f2fe', borderRadius: 4, opacity: coinMode === 'limited' ? 1 : 0.5, marginBottom: 10 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13 }}>
              <span style={{ color: '#0c4a6e', fontWeight: 500 }}>일일 플레이 가능 횟수:</span>
              <input
                type="number"
                min={1}
                max={100}
                step={1}
                value={coinDailyLimit}
                disabled={coinMode !== 'limited'}
                onChange={(e) => {
                  const raw = Number(e.target.value);
                  if (!Number.isFinite(raw)) return;
                  setCoinDailyLimit(Math.max(1, Math.min(100, Math.floor(raw))));
                }}
                style={{ width: 72, padding: '4px 8px', border: '1px solid var(--color-gray-300)', borderRadius: 4, fontSize: 13, textAlign: 'right' }}
              />
              <span style={{ color: 'var(--color-gray-500)', fontSize: 12 }}>회 (1~100, 기본 5)</span>
            </label>
          </div>

          <ul style={{ fontSize: 11, color: 'var(--color-gray-600)', paddingLeft: 18, margin: '0 0 10px', lineHeight: 1.8 }}>
            <li>중단해도 시작 시 사용한 횟수는 반환되지 않습니다.</li>
            <li>모드 전환 시 당일 사용 이력은 보존되며, 임의로 리셋되지 않습니다.</li>
            <li>설정 변경은 신규 세션부터 적용되며, 진행 중인 세션에 소급 적용되지 않습니다.</li>
            <li>코인 제한과 리더보드 점수 기록은 독립적으로 운영됩니다.</li>
          </ul>

          {coinErr && <div style={{ background: '#fee2e2', color: '#991b1b', padding: 8, borderRadius: 4, fontSize: 12, marginBottom: 8 }}>{coinErr}</div>}
          {coinMsg && <div style={{ background: '#dcfce7', color: '#166534', padding: 8, borderRadius: 4, fontSize: 12, marginBottom: 8 }}>{coinMsg}</div>}

          <button
            onClick={saveCoinPolicy}
            disabled={coinSaving || coinPolicyLoaded == null}
            style={{ padding: '8px 16px', fontSize: 13, fontWeight: 600, background: coinSaving ? 'var(--color-gray-300)' : '#0369a1', color: 'white', border: 'none', borderRadius: 4, cursor: coinSaving ? 'not-allowed' : 'pointer' }}
          >
            {coinSaving ? '저장 중…' : '코인 정책 저장'}
          </button>

          {coinHistory.length > 0 && (
            <details style={{ marginTop: 10 }}>
              <summary style={{ fontSize: 12, color: '#0369a1', cursor: 'pointer' }}>최근 변경 이력 ({coinHistory.length}건)</summary>
              <ul style={{ listStyle: 'none', padding: 0, margin: '8px 0 0', fontSize: 11, color: 'var(--color-gray-700)' }}>
                {coinHistory.map((h) => (
                  <li key={h.id} style={{ padding: '6px 10px', background: 'white', border: '1px solid #e0f2fe', borderRadius: 4, marginBottom: 4 }}>
                    <div><strong>{new Date(h.changedAt).toLocaleString()}</strong> · {h.changedByName || '-'}</div>
                    <div style={{ color: 'var(--color-gray-600)', marginTop: 2 }}>
                      {describeCoinPolicy(h.previous)} → {describeCoinPolicy(h.next)}
                    </div>
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>

        <div style={{ padding: 12, background: 'var(--color-gray-50)', borderRadius: 6, marginBottom: 12 }}>
          <strong style={{ fontSize: 13 }}>현재 리더보드 상태</strong>
          <div style={{ fontSize: 12, color: 'var(--color-gray-700)', marginTop: 6, lineHeight: 1.7 }}>
            <div>세대 (epoch): <code>{lbMeta?.epoch ?? '-'}</code></div>
            <div>최근 초기화: {lbMeta?.lastResetAt ? new Date(lbMeta.lastResetAt).toLocaleString() : '없음'}</div>
            <div>실행자: {lbMeta?.lastResetByName || '-'}</div>
            <div>사유: {lbMeta?.lastResetReason || '-'}</div>
          </div>
        </div>

        <h3 style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>리더보드 초기화</h3>
        <ul style={{ fontSize: 12, color: 'var(--color-gray-700)', lineHeight: 1.9, paddingLeft: 18, margin: '0 0 12px' }}>
          <li>영향 범위: 엔글 타자왕 TOP 10 및 전 임직원 개인 최고 점수</li>
          <li>보존: 과거 게임 플레이 이력 (acidRainSessions) 는 지워지지 않음</li>
          <li>세대(epoch) 방식: 초기화 시 epoch +1, 신규 기록만 집계됨 → 커밋 전 저장은 과거 세대로 남고 커밋 후 저장은 새 세대 반영 (원자적 처리)</li>
          <li>Phase 2 mock: sessionStorage 상의 개인 최고점만 리셋되고, 샘플 리더보드는 비워집니다 — 실제 서버 삭제는 Phase 3 에서 완성</li>
        </ul>
        {resetMsg && <div style={{ background: 'var(--color-green-50)', color: 'var(--color-green-700)', padding: 8, borderRadius: 6, fontSize: 12, marginBottom: 8 }}>{resetMsg}</div>}
        <button
          onClick={() => { setResetMsg(null); setResetStep(1); }}
          style={{ padding: '8px 16px', fontSize: 13, fontWeight: 600, background: 'var(--color-red-600, #dc2626)', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer' }}
        >
          리더보드 초기화…
        </button>
      </section>

      {/* Reset Confirm Modal — 2단계 */}
      {/* 게임 관리 — 엔글 마블 (Phase 2 Mock) */}
      <section style={{ padding: 16, border: '1px solid var(--color-gray-200)', borderRadius: 8, marginBottom: 24 }}>
        <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>게임 관리 — 엔글 마블</h2>
        <p style={{ color: 'var(--color-gray-600)', fontSize: 12, marginBottom: 16 }}>
          Phase 2 Mock — 서버 리더보드 초기화는 Phase 3 BE 에서 추가 예정. 현재는 오픈 기간 설정만 제공됩니다.
        </p>
        <OpenPeriodAdminSection
          gameLabel="엔글 마블"
          enabled={marbleEnabled}
          initialValue={marblePeriod}
          mockBadge="Phase 2 mock — localStorage"
          onSave={async (v) => {
            const r = await marbleApi.updateOpenPeriod(v);
            setMarblePeriod({ openStartAt: r.openStartAt, openEndAt: r.openEndAt });
          }}
        />

        {/* 주사위 정책 (V70 §B) */}
        <MarbleDicePolicySection />
      </section>

      {resetStep !== 0 && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'white', borderRadius: 8, padding: 24, width: 520, maxWidth: '90%' }}>
            {resetStep === 1 ? (
              <>
                <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 10 }}>리더보드 초기화 확인 (1/2)</h3>
                <div style={{ background: '#fef3c7', border: '1px solid #fde68a', color: '#92400e', padding: 10, borderRadius: 6, fontSize: 13, marginBottom: 12 }}>
                  <strong>⚠ 영향:</strong> TOP 10 + 전 임직원 개인 최고 점수가 리셋됩니다. 과거 게임 플레이 이력은 보존됩니다.
                </div>
                <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 6 }}>
                  초기화 사유 (5자 이상, 필수)
                </label>
                <textarea
                  value={resetReason}
                  onChange={(e) => setResetReason(e.target.value)}
                  placeholder="예: 2026년 11월 시즌 종료, 신규 게임대회 개시"
                  rows={3}
                  style={{ width: '100%', padding: 8, border: '1px solid var(--color-gray-300)', borderRadius: 4, fontSize: 13, boxSizing: 'border-box', resize: 'vertical' }}
                />
                {resetErr && <div style={{ background: '#fee2e2', color: '#991b1b', padding: 8, borderRadius: 4, fontSize: 12, marginTop: 8 }}>{resetErr}</div>}
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
                  <button onClick={closeResetModal} style={{ padding: '8px 16px', fontSize: 13, background: 'var(--color-gray-100)', border: '1px solid var(--color-gray-300)', borderRadius: 4, cursor: 'pointer' }}>
                    취소
                  </button>
                  <button
                    onClick={() => {
                      if (resetReason.trim().length < 5) {
                        setResetErr('사유는 5자 이상 입력해주세요.');
                        return;
                      }
                      setResetErr(null);
                      setResetStep(2);
                    }}
                    disabled={resetReason.trim().length < 5}
                    style={{ padding: '8px 16px', fontSize: 13, fontWeight: 600, background: resetReason.trim().length < 5 ? 'var(--color-gray-300)' : 'var(--color-primary)', color: 'white', border: 'none', borderRadius: 4, cursor: resetReason.trim().length < 5 ? 'not-allowed' : 'pointer' }}
                  >
                    다음 →
                  </button>
                </div>
              </>
            ) : (
              <>
                <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 10, color: '#dc2626' }}>최종 확인 (2/2)</h3>
                <div style={{ background: '#fee2e2', border: '1px solid #fecaca', color: '#991b1b', padding: 12, borderRadius: 6, fontSize: 13, marginBottom: 12, lineHeight: 1.7 }}>
                  이 작업은 즉시 적용됩니다. 정말로 리더보드를 초기화하시겠습니까?
                  <div style={{ marginTop: 8, color: '#7f1d1d', fontSize: 12 }}>
                    <strong>사유:</strong> {resetReason.trim()}
                  </div>
                </div>
                {resetErr && <div style={{ background: '#fee2e2', color: '#991b1b', padding: 8, borderRadius: 4, fontSize: 12, marginBottom: 8 }}>{resetErr}</div>}
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                  <button onClick={() => setResetStep(1)} disabled={resetBusy} style={{ padding: '8px 16px', fontSize: 13, background: 'var(--color-gray-100)', border: '1px solid var(--color-gray-300)', borderRadius: 4, cursor: resetBusy ? 'not-allowed' : 'pointer' }}>
                    ← 이전
                  </button>
                  <button onClick={closeResetModal} disabled={resetBusy} style={{ padding: '8px 16px', fontSize: 13, background: 'var(--color-gray-100)', border: '1px solid var(--color-gray-300)', borderRadius: 4, cursor: resetBusy ? 'not-allowed' : 'pointer' }}>
                    취소
                  </button>
                  <button
                    onClick={executeReset}
                    disabled={resetBusy}
                    style={{ padding: '8px 16px', fontSize: 13, fontWeight: 700, background: '#dc2626', color: 'white', border: 'none', borderRadius: 4, cursor: resetBusy ? 'not-allowed' : 'pointer' }}
                  >
                    {resetBusy ? '처리 중…' : '초기화 실행'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* 변경 이력 */}
      <section>
        <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 12 }}>최근 변경 이력 (최대 10건)</h2>
        {history.length === 0 && <div style={{ color: 'var(--color-gray-500)', fontSize: 13 }}>이력 없음</div>}
        <ul style={{ listStyle: 'none', padding: 0 }}>
          {history.map((h) => (
            <li key={h.id} style={{ padding: 10, border: '1px solid var(--color-gray-200)', borderRadius: 6, marginBottom: 6, fontSize: 12 }}>
              <div><strong>{h.changeType}</strong> · {h.changedAt} · {h.changedByName || '-'}</div>
              <div style={{ color: 'var(--color-gray-600)', marginTop: 4 }}>
                {h.previousSettings ? `${JSON.stringify(h.previousSettings)}` : '(최초 생성)'} → {JSON.stringify(h.newSettings)}
              </div>
            </li>
          ))}
        </ul>
      </section>

      {/* 직원 검색 Modal */}
      {showPicker && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'white', borderRadius: 8, padding: 20, width: 600, maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
            <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}>참가자 선택</h3>
            <input
              type="text"
              placeholder="이름 또는 사번"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ padding: 8, border: '1px solid var(--color-gray-300)', borderRadius: 4, marginBottom: 12, fontSize: 13 }}
            />
            <div style={{ flex: 1, overflowY: 'auto', border: '1px solid var(--color-gray-200)', borderRadius: 4 }}>
              {filteredEmployees.map((e) => (
                <label key={e.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 8, borderBottom: '1px solid var(--color-gray-100)', cursor: 'pointer', background: allowedUserIds.includes(e.id) ? 'var(--color-blue-50)' : 'white' }}>
                  <input type="checkbox" checked={allowedUserIds.includes(e.id)} onChange={() => toggleUser(e.id)} />
                  <div style={{ flex: 1, fontSize: 13 }}>
                    <strong>{e.nameKo}</strong> · {(e as any).employeeNumber || e.id.slice(0, 8)} · {(e as any).companyCode || '?'} · {(e as any).orgUnitName || '-'}
                  </div>
                </label>
              ))}
              {filteredEmployees.length === 0 && <div style={{ padding: 16, color: 'var(--color-gray-500)', fontSize: 13, textAlign: 'center' }}>검색 결과 없음</div>}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
              <button onClick={() => setShowPicker(false)} style={{ padding: '8px 16px', fontSize: 13, background: 'var(--color-primary)', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer' }}>완료 ({allowedUserIds.length}명)</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── V70 §B: 엔글 마블 주사위 정책 섹션 ───
function MarbleDicePolicySection() {
  const [loaded, setLoaded] = useState<{ mode: 'unlimited' | 'limited'; dailyLimit: number } | null>(null);
  const [mode, setMode] = useState<'unlimited' | 'limited'>('limited');
  const [dailyLimit, setDailyLimit] = useState<number>(3);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [history, setHistory] = useState<Awaited<ReturnType<typeof marbleApi.getDicePolicyHistory>>>([]);

  const load = useCallback(async () => {
    try {
      const [p, h] = await Promise.all([marbleApi.getDicePolicy(), marbleApi.getDicePolicyHistory({ limit: 10 })]);
      setLoaded(p); setMode(p.mode); setDailyLimit(p.dailyLimit); setHistory(h);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const handleSave = async () => {
    setErr(null); setMsg(null);
    if (mode === 'limited') {
      if (!Number.isInteger(dailyLimit) || dailyLimit < 1 || dailyLimit > 99) {
        setErr('일일 주사위 횟수는 1 이상의 정수여야 합니다 (최대 99).');
        return;
      }
    }
    setSaving(true);
    try {
      const reqId = `dicepol-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const r = await marbleApi.updateDicePolicy({ policy: { mode, dailyLimit }, clientRequestId: reqId });
      setMsg(`저장 완료 — ${r.next.mode === 'unlimited' ? '무제한' : `일일 ${r.next.dailyLimit}회 제한`}${r.isMock ? ' (mock)' : ''}`);
      await load();
    } catch (e: any) {
      setErr(e?.message || '저장 실패');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ padding: 14, background: '#fef9c3', border: '1px solid #facc15', borderRadius: 6, marginBottom: 12 }}>
      <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 8, color: '#854d0e' }}>엔글 마블 — 주사위 정책</h3>
      <div style={{ fontSize: 12, color: '#713f12', marginBottom: 10, lineHeight: 1.7 }}>
        현재 적용 중: <strong>
          {loaded == null ? '로딩 중…' : loaded.mode === 'unlimited' ? '무제한 (횟수 제한 없음)' : `제한 — 일일 ${loaded.dailyLimit}회`}
        </strong>
      </div>

      <div style={{ marginBottom: 10 }}>
        {[
          { v: 'limited', label: '제한 모드 (기본)', desc: '1인당 일일 주사위 횟수 지정. KST 00:00 리셋, 미사용 이월 없음.' },
          { v: 'unlimited', label: '무제한 모드', desc: '횟수 제한 없이 플레이. 공식 대회 공정성에 영향을 줄 수 있음.' },
        ].map((opt) => (
          <label key={opt.v} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '6px 0', cursor: 'pointer' }}>
            <input type="radio" name="dicePolicyMode" value={opt.v} checked={mode === opt.v}
              onChange={() => setMode(opt.v as 'unlimited' | 'limited')} style={{ marginTop: 3 }} />
            <div style={{ fontSize: 13 }}>
              <div style={{ fontWeight: 500, color: '#422006' }}>{opt.label}</div>
              <div style={{ color: '#78716c', fontSize: 11 }}>{opt.desc}</div>
            </div>
          </label>
        ))}
      </div>

      <div style={{ padding: '8px 12px', background: 'white', border: '1px solid #fef08a', borderRadius: 4, opacity: mode === 'limited' ? 1 : 0.5, marginBottom: 10 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13 }}>
          <span style={{ color: '#854d0e', fontWeight: 500 }}>일일 주사위 횟수:</span>
          <input
            type="number" min={1} max={99} step={1} value={dailyLimit}
            disabled={mode !== 'limited'}
            onChange={(e) => {
              const raw = Number(e.target.value);
              if (!Number.isFinite(raw)) return;
              setDailyLimit(Math.max(1, Math.min(99, Math.floor(raw))));
            }}
            style={{ width: 72, padding: '4px 8px', border: '1px solid var(--color-gray-300)', borderRadius: 4, fontSize: 13, textAlign: 'right' }}
          />
          <span style={{ color: '#78716c', fontSize: 12 }}>회 (1~99, 기본 3)</span>
        </label>
      </div>

      {/* 공식 대회 공정성 경고 (사용자 지시 §B.8) */}
      {mode === 'unlimited' && (
        <div style={{ padding: 10, background: '#fef2f2', border: '1px solid #fca5a5', color: '#991b1b', borderRadius: 4, fontSize: 12, marginBottom: 10 }}>
          ⚠ <strong>공식 대회 공정성 경고</strong> — 무제한 모드는 플레이어 간 공정성에 영향을 줄 수 있습니다. 테스트 환경 또는 운영자 재량 외에는 비권장.
        </div>
      )}

      <ul style={{ fontSize: 11, color: '#78716c', paddingLeft: 18, margin: '0 0 10px', lineHeight: 1.8 }}>
        <li>설정 변경 시 기존 사용 횟수 및 점수 보존 (사용자 지시 §B.5)</li>
        <li>mode 전환 시 remaining = max(0, dailyLimit - usedToday) 재계산 (§B.6)</li>
        <li>주사위를 굴릴 때만 1회 차감 — 이벤트/워프/결과에서 추가 차감 없음 (§B.8)</li>
        <li>중복 요청은 멱등 처리 (§B.11)</li>
      </ul>

      {err && <div style={{ background: '#fee2e2', color: '#991b1b', padding: 8, borderRadius: 4, fontSize: 12, marginBottom: 8 }}>{err}</div>}
      {msg && <div style={{ background: '#dcfce7', color: '#166534', padding: 8, borderRadius: 4, fontSize: 12, marginBottom: 8 }}>{msg}</div>}

      <button onClick={handleSave} disabled={saving || loaded == null}
        style={{ padding: '8px 16px', fontSize: 13, fontWeight: 600, background: saving ? 'var(--color-gray-300)' : '#ca8a04', color: 'white', border: 'none', borderRadius: 4, cursor: saving ? 'not-allowed' : 'pointer' }}>
        {saving ? '저장 중…' : '주사위 정책 저장'}
      </button>

      {history.length > 0 && (
        <details style={{ marginTop: 10 }}>
          <summary style={{ fontSize: 12, color: '#854d0e', cursor: 'pointer' }}>최근 변경 이력 ({history.length}건)</summary>
          <ul style={{ listStyle: 'none', padding: 0, margin: '8px 0 0', fontSize: 11, color: '#78716c' }}>
            {history.map((h) => (
              <li key={h.id} style={{ padding: '6px 10px', background: 'white', border: '1px solid #fef08a', borderRadius: 4, marginBottom: 4 }}>
                <div><strong>{new Date(h.changedAt).toLocaleString()}</strong> · {h.changedByName || '-'}</div>
                <div style={{ color: '#a16207', marginTop: 2 }}>
                  {h.previous.mode === 'unlimited' ? '무제한' : `제한(${h.previous.dailyLimit})`} → {h.next.mode === 'unlimited' ? '무제한' : `제한(${h.next.dailyLimit})`}
                </div>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

export default TournamentAdminSettingsPage;
